import clsx from 'clsx'

import { useMemo, useState, useEffect } from 'preact/hooks'
import { useBeaconLogClick } from 'libs/searchspring/hooks/useBeaconAnalytics'
import { scrollHistoryManager } from 'helpers/scroll-history'

import { ResultImage } from 'libs/searchspring/components/ResultImage'
import { Price } from 'libs/searchspring/components/Price'
import { useBadges } from '@/components/hooks/useBadges'
import { Badge } from 'libs/searchspring/components/Badge'
import { Promoline } from 'libs/searchspring/components/Promoline'
import { usePromolines } from '@/components/hooks/usePromolines'
import { useSwatches } from 'libs/searchspring/hooks/useSwatches'
import { ResultsSwatches } from 'libs/searchspring/components/ResultSwatches'
import { useGuides } from '@/components/hooks/useGuides'
import { ResultQuickAdd } from 'libs/searchspring/components/ResultQuickAdd'
import { hasB2BDiscount } from '@/components/custom-elements/b2b-price'

const DEFAULT_OPTIONS = {
  hideQuickAdd: false,
  hideSwatches: false,
  hidePromolines: false,
  hideBadges: false,
  hideWishlist: false
}

export function ResultsCard({ result, className, options = DEFAULT_OPTIONS, dataIndex = '0' }) {
  const [variant] = result.variants
  const isB2BCustomer = window.globals?.customer?.b2b
  const b2bPricing = window.globals?.customer?.b2bPricing
  const productId = result.uid || result.id?.toString?.() || result.id
  const b2bData = isB2BCustomer && b2bPricing ? b2bPricing[productId] : null
  const hasQuickB2bCheck = isB2BCustomer && b2bData && b2bData.price
  const [b2bPriceCheck, setB2bPriceCheck] = useState(hasQuickB2bCheck)

  const {
    hideQuickAdd = DEFAULT_OPTIONS.hideQuickAdd,
    hideSwatches = DEFAULT_OPTIONS.hideSwatches,
    hidePromolines = DEFAULT_OPTIONS.hidePromolines,
    hideBadges = DEFAULT_OPTIONS.hideBadges,
    hideWishlist = DEFAULT_OPTIONS.hideWishlist
  } = options
  const { currentResult, updateSwatch } = useSwatches(result)
  const clickLogger = useBeaconLogClick()

  const currentVariant = currentResult?.variants?.[0] || variant

  // For non-B2B customers, use regular pricing
  const finalPrice = currentVariant.price
  const finalCompareAtPrice = currentVariant.compare_at_price

  useEffect(() => {
    if (isB2BCustomer && currentVariant?.id) {
      hasB2BDiscount(currentVariant.id).then(hasDiscount => {
        setB2bPriceCheck(hasDiscount)
      }).catch(() => {
        setB2bPriceCheck(false)
      })
    } else {
      setB2bPriceCheck(false)
    }
  }, [isB2BCustomer, currentVariant?.id])

  const criteria = useMemo(() => ({
    tags: currentResult?.tags ?? [],
    collections: currentResult?.collection_handle ?? [],
    products: [currentResult.handle]
  }), [currentResult?.tags, currentResult?.collection_handle, currentResult?.handle])

  // Metaobject variables
  const allBadges = useBadges(criteria)
  const allPromolines = usePromolines(criteria)
  const [sizeGuide] = useGuides(criteria)

  // Generic function to get first two items with exclusive logic
  const getObjects = (items) => {
    const exclusiveItem = items.find(item => item.exclusive)
    if (exclusiveItem) {
      return [exclusiveItem, null]
    }
    return [items[0] || null, items[1] || null]
  }

  const [firstBadge, secondBadge] = useMemo(() => getObjects(allBadges), [allBadges])
  const [firstPromoline, secondPromoline] = useMemo(() => getObjects(allPromolines), [allPromolines])

  // Creates URL with collection handle reference if it exists
  const url = useMemo(() => {
    return `/products/${currentResult.handle}`
  }, [currentResult])

  // Analytics handling
  const handleLogClick = () => {
    // Collection is a global pulled from template-search.liquid and collection-main.liquid. Ensure value is set in both places :)
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(currentResult)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DON'T REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex', className)} id={`result__${currentResult.handle}`} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={dataIndex}>
      <div className="flex flex-col gap-y-0.5 md:gap-y-0.875 w-full grow">
        <div className="relative w-full overflow-hidden aspect-1/1">
          <a href={url} onClick={handleLogClick} aria-label={currentResult.name}>
            <ResultImage result={currentResult}>
              {!hideBadges && (firstBadge || secondBadge) && (
                <div className="absolute top-0.5 md:top-0.75 left-0.5 md:left-1 flex flex-col gap-y-0.125 max-w-[70%] z-2">
                  {firstBadge && <Badge badge={firstBadge} />}
                  {secondBadge && <Badge badge={secondBadge} />}
                </div>
              )}
            </ResultImage>
          </a>
          {/* Quick Add */}
          {(!hideQuickAdd) && (
            <div className="absolute bottom-0.625 lg:bottom-1 right-0.5 lg:right-1 w-fit lg:w-[calc(100%-2rem)]">
              {!hideQuickAdd && currentResult.is_giftcard !== 'true' && <ResultQuickAdd result={currentResult} firstPromoline={firstPromoline} secondPromoline={secondPromoline} sizeGuide={sizeGuide} />}
            </div>
          )}
          {/* WishlistKing Button */}
          {!hideWishlist && (
            <div className="wishlist-button flex items-center justify-center absolute top-0.5 md:top-0.625 right-0.5 lg:right-0.625 w-[20px] lg:w-[24px] aspect-1/1 z-2">
              <button type="button"
                data-product-handle={currentResult.handle}
                aria-label="Add to Wishlist"
                className="wkh-button save-wishlist flex items-center justify-center w-full h-full link-fade"
              >
                <div className="w-[11px] lg:w-[14px] h-[15px] lg:h-[18px] children:size-full">
                  <svg className="wkh-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="18" viewBox="0 0 14 18" fill="none">
                    <path d="M1 1H13V17L7 13.0414L1 17V1Z" fill="var(--icon-fill)" stroke="context-stroke" stroke-width="0.75px"/>
                  </svg>
                </div>
              </button>
            </div>
          )}
        </div>
        <div className="grow flex flex-col gap-y-1 px-0.5">
          <div className="grow flex flex-col gap-y-0.375">
            {!hideSwatches && <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />}
            <a href={url} className="paragraph-s tracking-0.96 uppercase link-fade" aria-label={currentResult.name} dangerouslySetInnerHTML={{ __html: currentResult.name }} />
          </div>
          <div className="flex min-h-1.25">
            {isB2BCustomer ? (
              <b2b-price
                class="group/price flex gap-x-0.25"
                data-variant-id={currentVariant.id}>
                <span data-original-price class="hidden group-data-[sale=true]/price:block line-through paragraph-b font-bold"></span>
                <span data-b2b-price class="group-data-[sale=true]/price:text-brand-red paragraph-b font-bold"></span>
              </b2b-price>
            ) : (
              <Price price={finalPrice} compareAtPrice={finalCompareAtPrice} className="flex gap-x-0.25" />
            )}
          </div>
          {!hidePromolines && (b2bPriceCheck || firstPromoline || secondPromoline) && (
            <div className="flex flex-col gap-0.25">
              {b2bPriceCheck && (
                <Promoline promoline={{
                  title: 'Wholesale Price',
                  text_colour: '#9C0000',
                  background_colour: '#FCEBEB'
                }} promolineLocation="PLP" promolineSize="medium" />
              )}
              {firstPromoline && <Promoline promoline={firstPromoline} promolineLocation="PLP" promolineSize="medium" />}
              {!b2bPriceCheck && secondPromoline && <Promoline promoline={secondPromoline} promolineLocation="PLP" promolineSize="medium" />}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
